import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import { format, isSameMonth, isWithinInterval, parseISO } from 'date-fns';
import { fromZonedTime, toZonedTime, format as formatTz } from 'date-fns-tz';

function Events() {
  const [events, setEvents] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [fullSizeImage, setFullSizeImage] = useState(null);
  const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  // Array of colors for event circles
  const eventColors = [
    '#0099ff', // bright blue
    '#3366cc', // medium blue
    '#33cc66', // bright green
    '#00aa77', // teal-green
    '#9933ff', // bright purple
    '#6633cc', // deep purple
  ];

  // Function to get a color based on the event title
  const getEventColor = (event) => {
    // Use the sum of char codes of the title to pick a color
    if (!event.title) return eventColors[0];

    const charSum = event.title
      .split('')
      .reduce((acc, char) => acc + char.charCodeAt(0), 0);

    return eventColors[charSum % eventColors.length];
  };

  useEffect(() => {
    // Fetch events from the events folder
    fetch('/data/events/events.json')
      .then((response) => response.json())
      .then((data) => {
        console.log(data);
        // Filter out past events (more than one day old)
        const now = new Date();
        const oneDayAgo = new Date(now.setDate(now.getDate() - 1));

        const filteredEvents = data.filter((event) => {
          if (event.date) {
            // Single date event
            const eventDate = new Date(event.date);
            return eventDate >= oneDayAgo;
          } else if (event.startDate && event.endDate) {
            // Date range event - keep if end date is not in the past
            const endDate = new Date(event.endDate);
            return endDate >= oneDayAgo;
          }
          return false;
        });

        console.log(filteredEvents);
        setEvents(filteredEvents);
      })
      .catch((error) => console.error('Error loading events:', error));
  }, []);

  const tileClassName = ({ date }) => {
    // Add a class to dates that have events (either single date or within a date range)
    const hasEvent = events.some((event) => {
      if (event.date) {
        // Single date event
        return (
          format(new Date(event.date), 'yyyy-MM-dd') ===
          format(date, 'yyyy-MM-dd')
        );
      } else if (event.startDate && event.endDate) {
        // Date range event
        const start = parseISO(event.startDate);
        const end = parseISO(event.endDate);
        return isWithinInterval(date, { start, end });
      }
      return false;
    });
    return hasEvent ? 'has-event' : null;
  };

  const getEventsForMonth = (date) => {
    return events
      .filter((event) => {
        if (event.date) {
          // Single date event
          return isSameMonth(new Date(event.date), date);
        } else if (event.startDate && event.endDate) {
          // Date range event - include if any part of the range falls within this month
          const startDate = new Date(event.startDate);
          const endDate = new Date(event.endDate);

          // Check if either start or end date is in the month, or if the range spans the month
          return (
            isSameMonth(startDate, date) ||
            isSameMonth(endDate, date) ||
            (startDate < date &&
              endDate > new Date(date.getFullYear(), date.getMonth() + 1, 0))
          );
        }
        return false;
      })
      .sort((a, b) => {
        // Sort by start date/time
        const dateA = a.date ? new Date(a.date) : new Date(a.startDate);
        const dateB = b.date ? new Date(b.date) : new Date(b.startDate);
        return dateA - dateB;
      });
  };

  const formatEventTime = (dateString) => {
    const utcDate = fromZonedTime(new Date(dateString), 'UTC');
    const userDate = toZonedTime(utcDate, userTimeZone);
    return formatTz(userDate, 'h:mm a z', { timeZone: userTimeZone });
  };

  const formatEventDate = (dateString) => {
    const utcDate = fromZonedTime(new Date(dateString), 'UTC');
    const userDate = toZonedTime(utcDate, userTimeZone);
    return formatTz(userDate, 'MMMM d, yyyy', { timeZone: userTimeZone });
  };

  const getDateRangeDisplay = (event) => {
    if (event.date) {
      return (
        <>
          <p className='text-primary-300 mb-2'>
            Date: {formatEventDate(event.date)}
          </p>
          <p className='text-primary-300 mb-2'>
            Time: {formatEventTime(event.date)}
          </p>
        </>
      );
    } else if (event.startDate && event.endDate) {
      return (
        <>
          <p className='text-primary-300 mb-2'>
            Start: {formatEventDate(event.startDate)} at{' '}
            {formatEventTime(event.startDate)}
          </p>
          <p className='text-primary-300 mb-2'>
            End: {formatEventDate(event.endDate)} at{' '}
            {formatEventTime(event.endDate)}
          </p>
        </>
      );
    }
    return null;
  };

  const openFullSizeImage = (imagePath) => {
    setFullSizeImage(imagePath);
  };

  const closeFullSizeImage = () => {
    setFullSizeImage(null);
  };

  const monthEvents = getEventsForMonth(selectedDate);

  return (
    <div className='container mx-auto px-4 py-8'>
      <Helmet>
        <title>Upcoming Events - Red Panda Treehouse</title>
        <meta name="description" content="Discover upcoming events, streams, and community gatherings at Red Panda Treehouse. Join our inclusive Vtuber community for exciting content and activities." />
        <meta name="keywords" content="vtuber events, streaming calendar, community events, red panda treehouse events, vtuber streams" />
        <link rel="canonical" href="https://redpandatreehouse.com/events" />

        {/* Open Graph */}
        <meta property="og:title" content="Upcoming Events - Red Panda Treehouse" />
        <meta property="og:description" content="Discover upcoming events, streams, and community gatherings at Red Panda Treehouse." />
        <meta property="og:url" content="https://redpandatreehouse.com/events" />
        <meta property="og:type" content="website" />

        {/* Twitter */}
        <meta name="twitter:title" content="Upcoming Events - Red Panda Treehouse" />
        <meta name="twitter:description" content="Discover upcoming events, streams, and community gatherings at Red Panda Treehouse." />
      </Helmet>
      <h1 className='text-3xl font-bold text-primary-600 mb-8'>
        Upcoming Events
      </h1>

      <div className='grid md:grid-cols-2 gap-8'>
        <div className='max-w-full overflow-x-auto'>
          <div className='calendar-container min-w-[280px] w-full max-w-[400px] mx-auto'>
            <Calendar
              onChange={setSelectedDate}
              value={selectedDate}
              tileClassName={tileClassName}
              calendarType='gregory'
              tileContent={({ date, view }) => {
                if (view !== 'month') return null;
                // Find matching events for this date
                const dayEvents = events.filter((event) => {
                  if (event.date) {
                    return (
                      format(new Date(event.date), 'yyyy-MM-dd') ===
                      format(date, 'yyyy-MM-dd')
                    );
                  } else if (event.startDate && event.endDate) {
                    const start = parseISO(event.startDate);
                    const end = parseISO(event.endDate);
                    return isWithinInterval(date, { start, end });
                  }
                  return false;
                });
                if (dayEvents.length === 0) return null;

                // Use the first matching event for the dot color
                const eventColor = getEventColor(dayEvents[0]);

                return (
                  <div
                    style={{
                      position: 'relative',
                      height: '100%',
                      width: '100%',
                    }}
                  >
                    <div
                      style={{
                        position: 'absolute',
                        top: '-15%', // Center it vertically
                        left: '50%',
                        transform: 'translateX(-50%)',
                        width: '8px',
                        height: '8px',
                        borderRadius: '50%', // Make it a circle
                        background: eventColor,
                        zIndex: 1,
                      }}
                    />
                  </div>
                );
              }}
              className='w-full rounded-lg shadow-lg'
            />
          </div>
        </div>
        <div>
          <h2 className='text-2xl font-semibold text-primary-500 mb-4'>
            Events for {format(selectedDate, 'MMMM yyyy')}
          </h2>

          {monthEvents.length > 0 ? (
            <div className='space-y-6'>
              {monthEvents.map((event, index) => (
                <div
                  key={index}
                  className='bg-white dark:bg-dark-card rounded-lg shadow-md p-6'
                >
                  <h3 className='text-xl font-semibold text-primary-400 mb-2'>
                    {event.title}
                  </h3>

                  {getDateRangeDisplay(event)}

                  {event.image && (
                    <div className='mb-4 event-image-container'>
                      <img
                        src={`/data/events/images/${event.image}`}
                        alt={event.title}
                        className='w-full h-48 object-cover rounded-lg cursor-pointer event-image'
                        onClick={() =>
                          openFullSizeImage(
                            `/data/events/images/${event.image}`
                          )
                        }
                      />
                    </div>
                  )}

                  <p className='text-primary-200 dark:text-primary-200 mb-4'>
                    {event.description}
                  </p>

                  {event.links && event.links.length > 0 && (
                    <div className='space-y-2'>
                      {event.links.map((link, linkIndex) => (
                        <div key={linkIndex}>
                          <a
                            href={link.url}
                            target='_blank'
                            rel='noopener noreferrer'
                            className='text-primary-100 hover:text-primary-100 underline block'
                          >
                            {link.title}
                          </a>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className='text-primary-700 dark:text-primary-400'>
              No events scheduled for this month.
            </p>
          )}
        </div>
      </div>

      {/* Full size image modal */}
      {fullSizeImage && (
        <div
          className='fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50'
          onClick={closeFullSizeImage}
        >
          <div className='max-w-4xl max-h-screen p-4 relative'>
            <button
              onClick={closeFullSizeImage}
              className='absolute top-2 right-2 bg-white rounded-full w-8 h-8 flex items-center justify-center'
            >
              ✕
            </button>
            <img
              src={fullSizeImage}
              alt='Full size event'
              className='max-w-full max-h-[90vh] object-contain'
            />
          </div>
        </div>
      )}

      {/* Add some custom styles for the calendar */}
      <style jsx global>{`
        .react-calendar {
          width: 100% !important;
          max-width: 100%;
          font-family: inherit;
          line-height: 1.125em;
        }

        .react-calendar__viewContainer {
          width: 100%;
        }

        .react-calendar__tile {
          position: relative;
          height: 40px; /* Reduce height slightly for mobile */
          padding: 0.5em 0.5em;
          font-size: 0.9em;
        }

        @media (max-width: 640px) {
          .react-calendar__tile {
            height: 35px;
            padding: 0.3em;
            font-size: 0.8em;
          }

          .react-calendar__navigation button {
            font-size: 0.9em;
            padding: 3px;
          }
        }

        .has-event {
          font-weight: bold;
        }

        .event-image-container {
          overflow: hidden;
          border-radius: 0.5rem;
        }

        .event-image {
          transition: transform 0.3s ease;
        }

        .event-image:hover {
          transform: scale(1.05);
        }

        .calendar-container {
          width: 100%;
        }
      `}</style>
    </div>
  );
}

export default Events;
